import { useState, useEffect } from "react";
import { Row, Col, Modal } from "react-bootstrap";
import CartModal from "../../../components/modal/CartMod";
import { OrderItem } from "../../../components/OrderItem";
import { hrStyle } from "../../../components/ShoppingCart";
import { formatCurrency, formatSignedCurrency } from "../../../utilities/formatCurrency";
import { getOrderCostLines, normalizeOrderCost } from "../../../lib/orderCost";
import { fetchAllOrders } from "../../../lib/webService/webService";
import { toast } from "react-toastify";
import { useAuthCheck } from "../../../utilities/authCheck";
import { useUserContext } from "../../../context/UserContext";
import { PullToRefreshWrapper } from "../../../components/pullRefresh";
import NotificationBell from "../../../components/admin/NotificationBell";
import { Seo } from "../../../components/Seo";
import { generateNoIndexSEO } from "../../../lib/seo/metaGenerators";
import {
  Cart,
  CashStack,
  Clock,
  CheckCircle,
  ArrowClockwise,
  ArrowRight,
  Receipt,
  Person,
  Telephone,
  XLg,
  Hourglass,
  Gear,
  Truck,
} from "react-bootstrap-icons";

// ─── Module-level CSS ────────────────────────────────────────────────────────
if (typeof document !== "undefined" && !document.getElementById("sm-styles")) {
  const s = document.createElement("style");
  s.id = "sm-styles";
  s.textContent = `
    @keyframes _sm_sk { 0%,100%{opacity:1}50%{opacity:.4} }
    ._sm_sk { animation: _sm_sk 1.8s ease-in-out infinite; background: rgba(60,60,67,0.08); }
    .sm-modal .modal-content { border-radius: 20px !important; border: none !important; overflow: hidden; }
    .sm-row { transition: background-color 0.15s ease; }
    .sm-row:hover { background-color: rgba(118,118,128,0.06); }
  `;
  document.head.appendChild(s);
}

const FONT =
  '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif';

const STATUS_CONFIG = {
  placed:     { color: "#8E8E93", icon: <Hourglass size={11} /> },
  accepted:   { color: "#007AFF", icon: <CheckCircle size={11} /> },
  processing: { color: "#FF9500", icon: <Gear size={11} /> },
  enroute:    { color: "#5856D6", icon: <Truck size={11} /> },
  delivered:  { color: "#34C759", icon: <CheckCircle size={11} /> },
  completed:  { color: "#34C759", icon: <CheckCircle size={11} /> },
};

const Skel = ({ w, h, r = 8, style = {} }) => (
  <div className="_sm_sk" style={{ width: w, height: h, borderRadius: r, ...style }} />
);

function SectionCard({ title, action, children }) {
  return (
    <div
      style={{
        background: "#FFFFFF",
        borderRadius: 16,
        border: "1px solid rgba(60,60,67,0.07)",
        padding: "20px",
        fontFamily: FONT,
      }}
    >
      {(title || action) && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          {title && (
            <p style={{ margin: 0, fontSize: "0.9375rem", fontWeight: 600, color: "#1C1C1E", letterSpacing: "-0.015em" }}>
              {title}
            </p>
          )}
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

function MetricCard({ label, value, Icon, accent, loading }) {
  if (loading) {
    return (
      <div style={{ background: "#FFFFFF", borderRadius: 16, border: "1px solid rgba(60,60,67,0.07)", padding: "20px", height: "100%" }}>
        <Skel w={40} h={40} r={10} />
        <Skel w="68%" h={11} r={4} style={{ marginTop: 16, marginBottom: 8 }} />
        <Skel w="54%" h={30} r={6} />
      </div>
    );
  }
  return (
    <div style={{ background: "#FFFFFF", borderRadius: 16, border: "1px solid rgba(60,60,67,0.07)", padding: "20px", height: "100%", fontFamily: FONT }}>
      <div style={{ width: 40, height: 40, borderRadius: 10, background: accent + "1A", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon size={18} color={accent} />
      </div>
      <p style={{ margin: "14px 0 5px", fontSize: "0.68rem", fontWeight: 600, color: "rgba(60,60,67,0.5)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {label}
      </p>
      <p style={{ margin: 0, fontSize: "1.625rem", fontWeight: 700, color: "#1C1C1E", letterSpacing: "-0.03em", lineHeight: 1.1 }}>
        {value}
      </p>
    </div>
  );
}

const StatusPill = ({ status }) => {
  const cfg = STATUS_CONFIG[status] ?? { color: "#8E8E93", icon: null };
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        padding: "3px 10px 3px 8px", borderRadius: 20,
        backgroundColor: `${cfg.color}1A`, color: cfg.color,
        fontSize: "0.7rem", fontWeight: 600, textTransform: "capitalize", letterSpacing: "0.01em",
      }}
    >
      {cfg.icon}
      {status}
    </span>
  );
};

function ShowOrderModal({ orderId, cost, cartItems, status }) {
  const [show, setShow] = useState(false);
  const total = normalizeOrderCost(cost).total;

  return (
    <>
      <button
        onClick={() => setShow(true)}
        style={{
          background: "none", border: "none", padding: 0,
          fontSize: "0.8125rem", fontWeight: 600, color: "#007AFF", cursor: "pointer",
        }}
      >
        View →
      </button>
      <Modal show={show} onHide={() => setShow(false)} centered dialogClassName="sm-modal">
        <Modal.Body style={{ padding: "24px", fontFamily: FONT }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
            <div>
              <h3 style={{ fontSize: "1.0625rem", fontWeight: 700, color: "#1c1c1e", margin: "0 0 4px", letterSpacing: "-0.02em" }}>
                Order Details
              </h3>
              <span style={{ fontSize: "0.78rem", color: "rgba(60,60,67,0.42)", fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>
                #{orderId}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <StatusPill status={status} />
              <button
                onClick={() => setShow(false)}
                style={{
                  width: 28, height: 28, borderRadius: "50%",
                  background: "rgba(118,118,128,.12)", border: "none",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", color: "rgba(60,60,67,0.55)", flexShrink: 0,
                }}
              >
                <XLg size={12} />
              </button>
            </div>
          </div>

          <div style={{ fontSize: "0.72rem", fontWeight: 600, color: "rgba(60,60,67,0.55)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>
            Order Items
          </div>
          <div style={{ marginBottom: 20 }}>
            {cartItems.map((item) => (
              <div key={item.id} className="mb-3">
                <OrderItem key={item.id} {...item} />
                <hr style={hrStyle} />
              </div>
            ))}
          </div>

          <div style={{ fontSize: "0.72rem", fontWeight: 600, color: "rgba(60,60,67,0.55)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>
            Order Summary
          </div>
          <div style={{ background: "rgba(118,118,128,.07)", borderRadius: 12, padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
            {getOrderCostLines(cost).map(({ label, value }) => (
              <div key={label} style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: "0.875rem", color: "rgba(60,60,67,0.52)" }}>{label}</span>
                <span style={{ fontSize: "0.875rem", fontWeight: 500, color: "#1c1c1e" }}>{formatSignedCurrency(value)}</span>
              </div>
            ))}
            <div style={{ borderTop: "1px solid rgba(60,60,67,0.1)", paddingTop: 10, marginTop: 2, display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontSize: "0.9375rem", fontWeight: 700, color: "#1c1c1e" }}>Total</span>
              <span style={{ fontSize: "1rem", fontWeight: 700, color: "#007AFF" }}>{formatCurrency(total)}</span>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

const OrderRow = ({ order }) => (
  <div
    className="sm-row"
    style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: "12px 8px", borderRadius: 12,
      borderBottom: "1px solid rgba(60,60,67,0.06)",
    }}
  >
    <div
      style={{
        width: 34, height: 34, borderRadius: 9, background: "rgba(0,122,255,0.10)",
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}
    >
      <Receipt size={14} color="#007AFF" />
    </div>

    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: "0.8125rem", fontWeight: 600, color: "#1c1c1e", marginBottom: 2 }}>
        #{order?.id}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.75rem", color: "rgba(60,60,67,0.5)" }}>
          <Person size={11} /> {order?.firstName} {order?.lastName}
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.75rem", color: "rgba(60,60,67,0.5)" }}>
          <Telephone size={11} /> {order?.phone}
        </span>
      </div>
    </div>

    <StatusPill status={order?.status} />

    <span style={{ fontSize: "0.875rem", fontWeight: 700, color: "#1c1c1e", minWidth: 74, textAlign: "right" }}>
      {formatCurrency(order?.cost.subTotal)}
    </span>

    <ShowOrderModal orderId={order.id} cost={order.cost} cartItems={order.cart} status={order.status} />
  </div>
);

const SupMain = () => {
  const { isAuthorized, isLoading: isAuthLoading } = useAuthCheck();
  const { token } = useUserContext();
  const [orders, setOrders] = useState([]);
  const [isOrdersLoading, setIsOrdersLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCartModal, setShowCartModal] = useState(false);
  const [stats, setStats] = useState({
    totalOrders: 0,
    revenue: 0,
    pendingOrders: 0,
    completedOrders: 0,
  });
  const [isStatsLoading, setIsStatsLoading] = useState(true);

  const handleCartClose = () => setShowCartModal(false);

  const calculateStats = (ordersData) => {
    const totalOrders = ordersData.length;
    const revenue = ordersData.reduce(
      (sum, order) => sum + order.cost.subTotal,
      0
    );
    const pendingOrders = ordersData.filter(
      (order) => order.status === "pending"
    ).length;
    const completedOrders = ordersData.filter(
      (order) => order.status === "completed"
    ).length;

    return {
      totalOrders,
      revenue,
      pendingOrders,
      completedOrders: Math.round((completedOrders / totalOrders) * 100) || 0,
    };
  };

  const fetchOrders = async () => {
    if (!token) return;
    try {
      setIsOrdersLoading(true);
      setError(null);
      const response = await fetchAllOrders(token);

      if (!response) {
        throw new Error("No data received from the server");
      }

      setOrders(response);
      setStats(calculateStats(response));
    } catch (error) {
      console.error("Error fetching orders:", error);
      setError(
        error.message || "Failed to fetch orders. Please try again later."
      );
      toast.error("Failed to load orders. Please refresh the page.");
    } finally {
      setIsOrdersLoading(false);
      setIsStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (isAuthLoading) {
    return (
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "28px 16px" }}>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
          <Skel w={40} h={40} r={20} />
        </div>
      </div>
    );
  }

  if (!isAuthorized) {
    return null; // Will be redirected by useAuthCheck
  }

  return (
    <PullToRefreshWrapper onRefresh={fetchOrders}>
      <Seo {...generateNoIndexSEO("Admin Dashboard")} />
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "20px 16px 48px", fontFamily: FONT }}>

        {/* Header */}
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <div>
            <h2 style={{ fontSize: "1.375rem", fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.03em", margin: "0 0 4px" }}>
              Dashboard Overview
            </h2>
            <p style={{ fontSize: "0.8125rem", color: "rgba(60,60,67,0.5)", margin: 0 }}>
              Welcome back! Here's what's happening with your orders today.
            </p>
          </div>
          {/* <button
            onClick={fetchOrders}
            disabled={isOrdersLoading}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "9px 16px", borderRadius: 20,
              border: "1px solid rgba(0,122,255,0.35)", backgroundColor: "transparent",
              color: "#007AFF", fontSize: "0.8125rem", fontWeight: 600,
              cursor: isOrdersLoading ? "not-allowed" : "pointer",
              opacity: isOrdersLoading ? 0.5 : 1,
              fontFamily: FONT, minWidth: 96, justifyContent: "center",
            }}
          >
            <ArrowClockwise size={14} /> Refresh
          </button> */}
          <NotificationBell />
        </div>

        {/* Error banner */}
        {error && (
          <div
            style={{
              backgroundColor: "rgba(255,59,48,0.08)", borderRadius: 12,
              padding: "12px 16px", marginBottom: 20,
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}
          >
            <span style={{ fontSize: "0.875rem", color: "#FF3B30" }}>{error}</span>
            <button
              onClick={fetchOrders}
              style={{ background: "none", border: "none", padding: 0, fontSize: "0.8125rem", color: "#007AFF", fontWeight: 600, cursor: "pointer" }}
            >
              Try Again
            </button>
          </div>
        )}

        {/* Stats bento grid */}
        <Row className="g-3 mb-4">
          <Col xs={6} md={3}>
            <MetricCard label="Total Orders" value={stats.totalOrders} Icon={Cart} accent="#007AFF" loading={isStatsLoading} />
          </Col>
          <Col xs={6} md={3}>
            <MetricCard label="Revenue" value={formatCurrency(stats.revenue)} Icon={CashStack} accent="#34C759" loading={isStatsLoading} />
          </Col>
          <Col xs={6} md={3}>
            <MetricCard label="Pending Orders" value={stats.pendingOrders} Icon={Clock} accent="#FF9500" loading={isStatsLoading} />
          </Col>
          <Col xs={6} md={3}>
            <MetricCard label="Completed" value={`${stats.completedOrders}%`} Icon={CheckCircle} accent="#5856D6" loading={isStatsLoading} />
          </Col>
        </Row>

        {/* Recent orders */}
        <SectionCard
          title="Recent Orders"
          action={
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.8125rem", fontWeight: 600, color: "#007AFF", border: "none" }}>
              View All <ArrowRight size={13} />
            </span>
          }
        >
          <p style={{ margin: "-8px 0 16px", fontSize: "0.8125rem", color: "rgba(60,60,67,0.5)" }}>
            Latest transactions and orders
          </p>

          {isOrdersLoading ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {[1, 2, 3].map((i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 8px" }}>
                  <Skel w={34} h={34} r={9} />
                  <div style={{ flex: 1 }}>
                    <Skel w="30%" h={13} r={5} style={{ marginBottom: 6 }} />
                    <Skel w="55%" h={11} r={4} />
                  </div>
                  <Skel w={70} h={20} r={20} />
                  <Skel w={60} h={16} r={5} />
                </div>
              ))}
            </div>
          ) : orders.length === 0 ? (
            <div style={{ textAlign: "center", padding: "32px 16px" }}>
              <p style={{ fontSize: "0.875rem", color: "rgba(60,60,67,0.45)", margin: 0 }}>No orders found</p>
            </div>
          ) : (
            <div>
              {orders.slice(0, 5).map((order) => (
                <OrderRow key={order?.id} order={order} />
              ))}
            </div>
          )}
        </SectionCard>
      </div>
    </PullToRefreshWrapper>
  );
};

export default SupMain;
